import React from 'react';
import { WorkflowState } from '../../../models/enums/WorkflowState';
import { ApprovalType } from '../../../models/enums/ApprovalType';
import WorkflowStateBadge from '../../common/WorkflowStateBadge';
import PolicyExemptionRequest from '../../../models/entities/PolicyExemptionRequest';
import './ProgressBarBig.css';

interface ProgressBarBigProps {
	workflowState: WorkflowState;
	policyExemptionRequest: PolicyExemptionRequest;
}

// States that come after a given approval step (meaning that step is complete)
const STATES_AFTER_SUBMITTED = [
	WorkflowState.PendingASAApproval,
	WorkflowState.PendingCSCApproval,
	WorkflowState.PendingForCyberApproval,
	WorkflowState.PendingAIApproval,
	WorkflowState.PendingAVDApproval,
	WorkflowState.PendingAKSACIApproval,
	WorkflowState.PendingDataIntegrationApproval,
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];

const STATES_AFTER_ASA = [
	WorkflowState.PendingCSCApproval,
	WorkflowState.PendingForCyberApproval,
	WorkflowState.PendingAIApproval,
	WorkflowState.PendingAVDApproval,
	WorkflowState.PendingAKSACIApproval,
	WorkflowState.PendingDataIntegrationApproval,
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];

const STATES_AFTER_CSC = [
	WorkflowState.PendingForCyberApproval,
	WorkflowState.PendingAIApproval,
	WorkflowState.PendingAVDApproval,
	WorkflowState.PendingAKSACIApproval,
	WorkflowState.PendingDataIntegrationApproval,
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];

const STATES_AFTER_CYBER = [
	WorkflowState.PendingAIApproval,
	WorkflowState.PendingAVDApproval,
	WorkflowState.PendingAKSACIApproval,
	WorkflowState.PendingDataIntegrationApproval,
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];

const STATES_AFTER_AI = [
	WorkflowState.PendingAVDApproval,
	WorkflowState.PendingAKSACIApproval,
	WorkflowState.PendingDataIntegrationApproval,
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];

const STATES_AFTER_AVD = [
	WorkflowState.PendingAKSACIApproval,
	WorkflowState.PendingDataIntegrationApproval,
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];

const STATES_AFTER_AKSACI = [
	WorkflowState.PendingDataIntegrationApproval,
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];

const STATES_AFTER_DATA_INTEGRATION = [
	WorkflowState.AwaitingImplementation,
	WorkflowState.Completed,
	WorkflowState.Error,
];
const STATES_AFTER_IMPLEMENTATION = [WorkflowState.Completed];
const DATA_INTEGRATION_POLICY_NAME = 'Disallow Microsoft Fabric Resource Types';

const isActive = (currentValue: number, states: WorkflowState[]): boolean =>
	states.some((s) => s.value === currentValue);

// TODO: fill in which additional approval types require each of these steps
const AI_APPROVAL_TYPES: number[] = [ApprovalType.AI_Manager.value];
const AVD_APPROVAL_TYPES: number[] = [ApprovalType.AVD_Manager.value];
const AKSACI_APPROVAL_TYPES: number[] = [ApprovalType.AKI_ACI_Manager.value];

const ProgressBarBig = ({ workflowState, policyExemptionRequest }: ProgressBarBigProps) => {
	const noProgressStates = [WorkflowState.Cancelled, WorkflowState.Rejected];
	const hasProgressBar = !noProgressStates.some((s) => s.value === workflowState.value);

	const cv = workflowState.value;
	const isTemporary = policyExemptionRequest?.durationRequested === 'temporary';
	const isAwaitingImplementation = cv === WorkflowState.AwaitingImplementation.value;
	const approvalTypeValue = policyExemptionRequest?.approvalType?.value;
	const policyNames = (policyExemptionRequest?.policyExemptionDefinitions || []).flatMap((definition) => [
		definition.displayName,
		definition.name,
	]);
	const requiresDataIntegrationApproval = policyNames.some(
		(name) => name?.toLowerCase() === DATA_INTEGRATION_POLICY_NAME.toLowerCase(),
	);

	const implementationActiveClass = isActive(cv, STATES_AFTER_IMPLEMENTATION)
		? 'per-big-back-blue'
		: isAwaitingImplementation
			? 'per-big-back-yellow'
			: '';

	const allSteps = [
		{
			label: 'Submitted',
			activeClass: isActive(cv, STATES_AFTER_SUBMITTED) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_SUBMITTED),
		},
		{
			label: 'ASA Approval',
			activeClass: isActive(cv, STATES_AFTER_ASA) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_ASA),
		},
		{
			label: 'CSC Approval',
			activeClass: isActive(cv, STATES_AFTER_CSC) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_CSC),
			temporaryOnly: false,
		},
		{
			label: 'Cyber Approval',
			activeClass: isActive(cv, STATES_AFTER_CYBER) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_CYBER),
			temporaryOnly: false,
		},
		{
			label: 'AI Approval',
			activeClass: isActive(cv, STATES_AFTER_AI) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_AI),
			requiredApprovalTypes: AI_APPROVAL_TYPES,
		},
		{
			label: 'AVD Approval',
			activeClass: isActive(cv, STATES_AFTER_AVD) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_AVD),
			requiredApprovalTypes: AVD_APPROVAL_TYPES,
		},
		{
			label: 'AKS/ACI Approval',
			activeClass: isActive(cv, STATES_AFTER_AKSACI) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_AKSACI),
			requiredApprovalTypes: AKSACI_APPROVAL_TYPES,
		},
		{
			label: 'Data Integration Approval',
			activeClass: isActive(cv, STATES_AFTER_DATA_INTEGRATION) ? 'per-big-back-blue' : '',
			showTick: isActive(cv, STATES_AFTER_DATA_INTEGRATION),
			requiredForDataIntegration: true,
		},
		{
			label: 'Implementation',
			activeClass: implementationActiveClass,
			showTick: isActive(cv, STATES_AFTER_IMPLEMENTATION),
		},
	];

	const steps = allSteps.filter((s) => {
		if ('temporaryOnly' in s && isTemporary) return false;
		if ('requiredApprovalTypes' in s && !s.requiredApprovalTypes.includes(approvalTypeValue)) return false;
		if ('requiredForDataIntegration' in s && !requiresDataIntegrationApproval) return false;
		return true;
	});

	const circleRadius = 15; // half of 30px circle width

	return (
		<>
			<div className="per-big-progress-bar__title">
				<span className="em-c-field__label em-c-field__label--margin">Status</span>
				<WorkflowStateBadge workflowState={workflowState} />
			</div>
			{hasProgressBar && (
				<div className="per-big-progress-bar">
					<div className="per-big-progress-bar__line" />
					{steps.map((step, i) => {
						const pct = steps.length > 1 ? (i / (steps.length - 1)) * 100 : 50;
						return (
							<div
								key={step.label}
								className="per-big-progress-bar__circle-with-title"
								style={{ left: `calc(${pct}% - ${circleRadius}px)` }}
							>
								<div
									className={`per-big-progress-bar__circle${step.activeClass ? ` ${step.activeClass}` : ''}`}
								>
									{step.showTick ? '✓' : ''}
								</div>
								<div className="per-big-progress-bar__circle--title">{step.label}</div>
							</div>
						);
					})}
				</div>
			)}
		</>
	);
};

export default ProgressBarBig;
